import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<{available: boolean; options?: string[]}>} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      // Don't pre-select any variants - start with empty checked indices
      this.#checkedIndices.push([]);

      // Initialize selected value display if there's a checked input
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement) {
        this.updateSelectedValueDisplay(fieldset, checkedInput);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    this.#variants = this.#readVariantsFromDom();
    this.#updateOptionAvailability();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
    this.#updateOptionAvailability();

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }
    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          // Update the selected value display in the legend
          this.updateSelectedValueDisplay(fieldset, target);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');

      // Update the selected value display for dropdowns
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        this.updateSelectedValueDisplay(fieldset, newSelectedOption);
      }
    }
  }

  /**
   * Updates the selected value display in the legend.
   * @param {HTMLElement} fieldset - The fieldset element.
   * @param {HTMLElement} selectedOption - The selected option element.
   */
  updateSelectedValueDisplay(fieldset, selectedOption) {
    const selectedValueSpan = fieldset.querySelector('.variant-option__selected-value');
    if (!selectedValueSpan) return;

    // Get the selected value text
    let selectedValueText = '';
    if (selectedOption instanceof HTMLInputElement) {
      const label = selectedOption.closest('label');
      const textSpan = label?.querySelector('.variant-option__button-label__text');
      selectedValueText = textSpan?.textContent?.trim() || selectedOption.value || '';
    } else if (selectedOption instanceof HTMLOptionElement) {
      selectedValueText = selectedOption.textContent?.trim().split(' - ')[0] || selectedOption.value || '';
    }

    // Update the selected value span
    if (selectedValueText) {
      selectedValueSpan.textContent = selectedValueText;
    } else {
      selectedValueSpan.textContent = '';
    }

    // Update the colon display by toggling a class on the name span
    const nameSpan = fieldset.querySelector('.variant-option__name');
    if (nameSpan) {
      if (selectedValueText) {
        nameSpan.classList.add('has-selected-value');
      } else {
        nameSpan.classList.remove('has-selected-value');
      }
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          this.dispatchEvent(
            new CustomEvent('variant:fetch-incomplete', {
              bubbles: true,
              detail: { productId: this.dataset.productId ?? '' },
            })
          );
          return;
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          this.dispatchEvent(
            new CustomEvent('variant:fetch-error', {
              bubbles: true,
              detail: { productId: this.dataset.productId ?? '' },
            })
          );
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Preserve the currently checked inputs before morphing
    const checkedInputs = Array.from(this.querySelectorAll('input[type="radio"]:checked')).filter(
      (input) => input instanceof HTMLInputElement
    );
    const checkedInputData = checkedInputs.map((input) => ({
      optionValueId: input.dataset.optionValueId,
      fieldsetIndex: input.dataset.fieldsetIndex,
      inputIndex: input.dataset.inputIndex,
    }));

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Re-initialize the component state after morphing
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
      this.#checkedIndices.push([]);
    });

    this.#variants = this.#readVariantsFromDom();

    // Restore the checked state after morphing
    checkedInputData.forEach((data) => {
      const input = this.querySelector(
        `input[data-option-value-id="${data.optionValueId}"][data-fieldset-index="${data.fieldsetIndex}"][data-input-index="${data.inputIndex}"]`
      );
      if (input instanceof HTMLInputElement) {
        input.checked = true;
        this.updateSelectedOption(input);
      }
    });

    // Initialize selected value display for all fieldsets after morphing
    fieldsets.forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement) {
        this.updateSelectedValueDisplay(fieldset, checkedInput);
      }
    });

    this.#updateOptionAvailability();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Reads variants JSON from the DOM if available.
   * @returns {Array<{available: boolean; options?: string[]}>}
   */
  #readVariantsFromDom() {
    const script = this.querySelector('script[type="application/json"][data-variant-picker-variants]');
    if (!script) return [];
    try {
      const data = JSON.parse(script.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to parse variant picker variants JSON:', error);
      return [];
    }
  }

  /**
   * Gets selected option values keyed by option position.
   * @returns {Record<number, string>}
   */
  #getSelectedOptionsByPosition() {
    /** @type {Record<number, string>} */
    const selectedOptions = {};

    const checkedRadios = this.querySelectorAll('fieldset input[type="radio"]:checked');
    checkedRadios.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const inputId = input.dataset.inputId;
      if (!inputId) return;
      const position = Number.parseInt(inputId.split('-')[0] || '', 10) - 1;
      if (Number.isNaN(position)) return;
      selectedOptions[position] = input.value;
    });

    const selects = this.querySelectorAll('select');
    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const option = select.selectedOptions[0];
      if (!option) return;
      const inputId = option.dataset.inputId;
      if (!inputId) return;
      const position = Number.parseInt(inputId.split('-')[0] || '', 10) - 1;
      if (Number.isNaN(position)) return;
      selectedOptions[position] = option.value;
    });

    return selectedOptions;
  }

  /**
   * Checks if a value is available based on current selection.
   * @param {number} optionPosition
   * @param {string} optionValue
   * @param {Record<number, string>} selectedOptions
   * @returns {boolean}
   */
  #isOptionValueAvailable(optionPosition, optionValue, selectedOptions) {
    if (!Array.isArray(this.#variants) || this.#variants.length === 0) return true;
    return this.#variants.some((variant) => {
      if (!variant || !variant.available || !Array.isArray(variant.options)) return false;
      if (variant.options[optionPosition] !== optionValue) return false;
      return variant.options.every((option, index) => {
        if (index === optionPosition) return true;
        const selectedValue = selectedOptions[index];
        return !selectedValue || selectedValue === option;
      });
    });
  }

  /**
   * Updates option availability UI for radios and selects.
   */
  #updateOptionAvailability() {
    if (!Array.isArray(this.#variants) || this.#variants.length === 0) return;

    const selectedOptions = this.#getSelectedOptionsByPosition();

    const radioInputs = this.querySelectorAll('input[type="radio"][data-input-id]');
    radioInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const inputId = input.dataset.inputId;
      if (!inputId) return;
      const position = Number.parseInt(inputId.split('-')[0] || '', 10) - 1;
      if (Number.isNaN(position)) return;
      const isAvailable = this.#isOptionValueAvailable(position, input.value, selectedOptions);
      input.dataset.optionAvailable = isAvailable ? 'true' : 'false';
      if (isAvailable) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
    });

    const selects = this.querySelectorAll('select');
    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      Array.from(select.options).forEach((option) => {
        const inputId = option.dataset.inputId;
        if (!inputId) return;
        const position = Number.parseInt(inputId.split('-')[0] || '', 10) - 1;
        if (Number.isNaN(position)) return;
        const isAvailable = this.#isOptionValueAvailable(position, option.value, selectedOptions);
        option.disabled = !isAvailable;
      });
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
